import { useEffect, useRef } from 'react'

import {
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableNode
} from '@lexical/table'
import { $getNearestNodeFromDOMNode } from 'lexical'

import { TableResizer } from '@eastcoal/common/components/TableResizer'
import { TableResizerChangePayload } from '@eastcoal/common/components/TableResizer/interface'

import { useEditor } from '../../hooks/useEditor'
import { $findParentNodeOrThrow } from '../../utils/node'

import {
  TABLE_COLUMN_WIDTH_CHANGE_COMMAND,
  TABLE_ROW_HEIGHT_CHANGE_COMMAND
} from './commands'
import { TableCellResizerPluginProps } from './interface'

export const MIN_COLUMN_WIDTH = 60
export const MIN_ROW_HEIGHT = 32

export const TableCellResizerPlugin = ({
  isMatchedTarget
}: TableCellResizerPluginProps) => {
  const editor = useEditor()
  const rootElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    editor.registerRootListener(rootElement => {
      rootElementRef.current = rootElement
    })
  }, [editor])

  // 设置默认列宽、行高
  useEffect(() => {
    return editor.registerNodeTransform(TableNode, tableNode => {
      if (tableNode.getColWidths()) {
        return tableNode
      }
      tableNode.setColWidths(
        Array.from(
          { length: tableNode.getColumnCount() },
          () => MIN_COLUMN_WIDTH
        )
      )
      return tableNode
    })
  }, [editor])

  const handleColWidthChange = ({
    cellElement,
    index,
    value
  }: TableResizerChangePayload) => {
    editor.update(() => {
      const cellNode = $getNearestNodeFromDOMNode(cellElement)
      if (!$isTableCellNode(cellNode)) {
        throw new Error('TableCellResizer: CellNode not found')
      }
      const tableNode = $findParentNodeOrThrow(cellNode, $isTableNode)
      const colWidths =
        tableNode.getColWidths() ??
        Array.from(
          { length: tableNode.getColumnCount() },
          () => MIN_COLUMN_WIDTH
        )
      const newColWidths = [...colWidths]
      const realColWidth = Math.max(value, MIN_COLUMN_WIDTH)
      newColWidths[index] = realColWidth
      tableNode.setColWidths(newColWidths)
      editor.dispatchCommand(TABLE_COLUMN_WIDTH_CHANGE_COMMAND, {
        index,
        width: realColWidth
      })
    })
  }

  const handleRowHeightChange = ({
    cellElement,
    index,
    value
  }: TableResizerChangePayload) => {
    editor.update(() => {
      const cellNode = $getNearestNodeFromDOMNode(cellElement)
      if (!$isTableCellNode(cellNode)) {
        throw new Error('TableCellResizer: CellNode not found')
      }
      const rowNode = $findParentNodeOrThrow(cellNode, $isTableRowNode)
      const realHeight = Math.max(value, MIN_ROW_HEIGHT)
      rowNode.setHeight(realHeight)
      editor.dispatchCommand(TABLE_ROW_HEIGHT_CHANGE_COMMAND, {
        index,
        height: realHeight
      })
    })
  }

  return (
    <TableResizer
      isMatchedTarget={isMatchedTarget}
      rootElement={rootElementRef}
      onColWidthChange={handleColWidthChange}
      onRowHeightChange={handleRowHeightChange}
    />
  )
}
